import React, { useState, useEffect } from 'react';
import '../AssessmentForm.css';

/**
 * ClinicalNotesTriageFollowUpTab
 *
 * Handles clinical notes for Triage Follow Up reports
 * Maps to TriageDetails.Report table fields
 *
 * Report types: 2003 (Triage Follow Up)
 * Section: psych-triage-followup-clinical-notes
 */

const ClinicalNotesTriageFollowUpTab = ({ reportId, api, onSave , isPublished}) => {
  // Form state
  const [agenda, setAgenda] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');
  const [homeworkReview, setHomeworkReview] = useState('');

  // UI state
  const [loading, setLoading] = useState(true);
  const [savingStates, setSavingStates] = useState({});
  const [saveTimeouts, setSaveTimeouts] = useState({});
  const [initialCharCounts, setInitialCharCounts] = useState({});

  useEffect(() => {
    loadClinicalNotesData();
  }, [reportId]);

  const loadClinicalNotesData = async () => {
    try {
      setLoading(true);
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-triage-followup-clinical-notes`);

      if (response.ok) {
        const data = await response.json();
        setAgenda(data.agenda || '');
        setSessionNotes(data.sessionNotes || '');
        setHomeworkReview(data.homeworkReview || '');

        // Set loaded fields as 'saved' to show green indicator
        const initialSavingStates = {};
        if (data.agenda) initialSavingStates['Agenda'] = 'saved';
        if (data.sessionNotes) initialSavingStates['SessionNotes'] = 'saved';
        if (data.homeworkReview) initialSavingStates['HomeworkReview'] = 'saved';
        setSavingStates(initialSavingStates);
      }
    } catch (error) {
      console.error('Error loading clinical notes data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (fieldName, value) => {
    if (isPublished) return;
    setSavingStates(prev => ({ ...prev, [fieldName]: 'saving' }));

    try {
      const response = await api.webAPIFetch(`/api/PatientCaseReports/${reportId}/sections/psych-triage-followup-clinical-notes/fields/${fieldName}`, {
        method: 'PATCH',
        body: JSON.stringify(value)
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      setSavingStates(prev => ({ ...prev, [fieldName]: 'saved' }));
      if (onSave) onSave();
    } catch (error) {
      console.error(`Error saving ${fieldName}:`, error);
      setSavingStates(prev => ({ ...prev, [fieldName]: 'error' }));
    }
  };

  const getSaveIndicator = (fieldName) => {
    const state = savingStates[fieldName];

    const fieldValues = {
      'Agenda': agenda,
      'SessionNotes': sessionNotes,
      'HomeworkReview': homeworkReview
    };

    const fieldValue = fieldValues[fieldName];
    const hasValue = fieldValue !== null && fieldValue !== undefined && fieldValue !== '';

    if (state === 'saving' && hasValue) {
      return (
        <span className="save-status saving" aria-label="Saving">
          <span className="spinner-small"></span>
        </span>
      );
    }
    if (state === 'saved' && hasValue) {
      return (
        <span className="save-status saved" aria-label="Saved">
          <span className="checkmark">✓</span>
        </span>
      );
    }
    if (state === 'error') {
      return (
        <span className="save-status error" aria-label="Error saving">
          <span className="error-mark">⚠</span>
        </span>
      );
    }
    return <span className="save-status" aria-hidden="true"></span>;
  };

  const handleFieldChange = (fieldName, value, setter) => {
    setter(value);

    // Track initial character count if not already tracking
    if (initialCharCounts[fieldName] === undefined) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
    }

    // Calculate characters typed since start
    const charsSinceStart = Math.abs(value.length - (initialCharCounts[fieldName] || 0));

    // Clear existing timeout
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    // Trigger save if 50+ characters typed
    if (charsSinceStart >= 50) {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
      return;
    }

    // Debounce save - 10 seconds after typing stops
    const timeoutId = setTimeout(() => {
      setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));
      saveField(fieldName, value);
    }, 10000);

    setSaveTimeouts(prev => ({ ...prev, [fieldName]: timeoutId }));
  };

  const handleFieldBlur = (fieldName, value) => {
    // Clear debounce timeout on blur
    if (saveTimeouts[fieldName]) {
      clearTimeout(saveTimeouts[fieldName]);
    }

    // Reset character count for next typing session
    setInitialCharCounts(prev => ({ ...prev, [fieldName]: value.length }));

    // Save immediately on blur
    saveField(fieldName, value);
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading clinical notes...</p>
      </div>
    );
  }

  return (
    <div className="assessment-container">
      <fieldset disabled={isPublished} style={{ border: "none", padding: 0, margin: 0 }}>
      <div className="form-section">
        <h3>Clinical Notes - Follow Up</h3>

        <div className="form-group">
          <label htmlFor="agenda">
            Agenda:
            {getSaveIndicator('Agenda')}
          </label>
          <textarea
            id="agenda"
            value={agenda}
            onChange={(e) => handleFieldChange('Agenda', e.target.value, setAgenda)}
            onBlur={() => handleFieldBlur('Agenda', agenda)}
            rows="4"
            style={{ width: '100%' }}
            placeholder="Enter the agenda for this session"
          />
        </div>

        <div className="form-group">
          <label htmlFor="sessionNotes">
            Session notes:
            {getSaveIndicator('SessionNotes')}
          </label>
          <p className="field-help-text" style={{ margin: '0 0 8px 0', color: '#666', fontSize: '0.9em' }}>
            (include what was covered in-session)
          </p>
          <textarea
            id="sessionNotes"
            value={sessionNotes}
            onChange={(e) => handleFieldChange('SessionNotes', e.target.value, setSessionNotes)}
            onBlur={() => handleFieldBlur('SessionNotes', sessionNotes)}
            rows="6"
            style={{ width: '100%' }}
            placeholder="Describe what was covered in this session"
          />
        </div>

        <div className="form-group">
          <label htmlFor="homeworkReview">
            Homework review:
            {getSaveIndicator('HomeworkReview')}
          </label>
          <textarea
            id="homeworkReview"
            value={homeworkReview}
            onChange={(e) => handleFieldChange('HomeworkReview', e.target.value, setHomeworkReview)}
            onBlur={() => handleFieldBlur('HomeworkReview', homeworkReview)}
            rows="4"
            style={{ width: '100%' }}
            placeholder="Review of homework assigned from previous session"
          />
        </div>
      </div>
      </fieldset>

    </div>
  );
};

export default ClinicalNotesTriageFollowUpTab;
